/**
 * Custom-schedule calendar picker — port of NotifCustomSchedule from
 * `screens/settings_notifications.jsx`. User picks one or more dates
 * of the month; if exactly one date is picked, an additional radio
 * lets them switch from "every month on these dates" to "every month
 * on the Nth weekday" (e.g. "1st Monday").
 */

import { useEffect, useMemo, useState } from "react";
import { Btn } from "@/components/Btn";
import type { NotifSchedule } from "@/lib/api-hooks";

const ORDINALS = ["1st", "2nd", "3rd", "4th", "5th"];

export function CustomScheduleModal({
  open,
  value,
  onCancel,
  onApply,
}: {
  open: boolean;
  value: NotifSchedule | null;
  onCancel: () => void;
  onApply: (next: NotifSchedule) => void;
}) {
  const today = useMemo(() => new Date(), []);
  const year = today.getFullYear();
  const month = today.getMonth();

  const [dates, setDates] = useState<number[]>([]);
  const [mode, setMode] = useState<"dates" | "ordinal">("dates");

  useEffect(() => {
    if (!open) return;
    if (value && value.kind === "custom") {
      setDates(value.dates ?? []);
      setMode(value.mode);
    } else {
      setDates([]);
      setMode("dates");
    }
  }, [open, value]);

  const single = dates.length === 1;
  const ordinalLabel = useMemo(() => {
    if (!single) return "";
    const d = new Date(year, month, dates[0]);
    const wd = d.toLocaleString("en", { weekday: "long" });
    const occurrence = Math.ceil(dates[0] / 7);
    const ord = ORDINALS[occurrence - 1] ?? `${occurrence}th`;
    return `${ord} ${wd}`;
  }, [single, dates, year, month]);

  if (!open) return null;

  const monthName = new Date(year, month).toLocaleString("en", {
    month: "long",
    year: "numeric",
  });
  // JS getDay: 0=Sun..6=Sat. We want Mo=1..Su=7 columns.
  const firstDayJs = new Date(year, month, 1).getDay();
  const firstDay = firstDayJs === 0 ? 7 : firstDayJs;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: (number | null)[] = [];
  for (let i = 1; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const toggleDate = (d: number) => {
    setDates((arr) =>
      arr.includes(d)
        ? arr.filter((x) => x !== d)
        : [...arr, d].sort((a, b) => a - b),
    );
  };

  const onApplyClick = () => {
    if (dates.length === 0) return;
    if (mode === "ordinal" && ordinalLabel) {
      onApply({
        kind: "custom",
        mode: "ordinal",
        ordinal_label: ordinalLabel,
        dates,
      });
    } else {
      onApply({ kind: "custom", mode: "dates", dates });
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,30,.45)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 380,
          background: "var(--c-surface)",
          borderRadius: 14,
          padding: 20,
          boxShadow: "var(--sh-3)",
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>
          Custom schedule
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: "var(--c-text-2)",
            marginBottom: 8,
          }}
        >
          {monthName}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 4,
            marginBottom: 16,
          }}
        >
          {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
            <div
              key={d}
              style={{
                fontSize: 11,
                textAlign: "center",
                color: "var(--c-text-3)",
                padding: "4px 0",
              }}
            >
              {d}
            </div>
          ))}
          {cells.map((c, i) =>
            c == null ? (
              <div key={`gap-${i}`} />
            ) : (
              <button
                key={`d-${c}`}
                type="button"
                onClick={() => toggleDate(c)}
                style={{
                  aspectRatio: "1",
                  border: 0,
                  borderRadius: 8,
                  fontSize: 12,
                  background: dates.includes(c)
                    ? "var(--c-accent-600)"
                    : "transparent",
                  color: dates.includes(c) ? "#fff" : "var(--c-text)",
                  cursor: "pointer",
                  fontWeight: dates.includes(c) ? 600 : 400,
                }}
              >
                {c}
              </button>
            ),
          )}
        </div>
        {dates.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 14,
            }}
          >
            <label className="check">
              <input
                type="radio"
                checked={mode === "dates"}
                onChange={() => setMode("dates")}
              />{" "}
              Send every month on these dates ({dates.join(", ")})
            </label>
            {single && (
              <label className="check">
                <input
                  type="radio"
                  checked={mode === "ordinal"}
                  onChange={() => setMode("ordinal")}
                />{" "}
                Send every month on the {ordinalLabel}
              </label>
            )}
          </div>
        )}
        <div
          style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}
        >
          <Btn onClick={onCancel}>Cancel</Btn>
          <Btn
            variant="primary"
            disabled={dates.length === 0}
            onClick={onApplyClick}
          >
            Apply
          </Btn>
        </div>
      </div>
    </div>
  );
}
